import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react'; // [FLUID-6 2026-10-12] useRef
import { createPortal } from 'react-dom';
import styled from 'styled-components';
import { lgSurface, lgPress, lgControlBg, lgCardSheen, lgTile, LG_PRESS, LgPressProps } from '../styles/liquidGlass'; // [LIQUID-GLASS 2026-11-02]
import {
  fetchPmRecommendations, peekCache, PmRecommendationsData,
  RecCohortRow, RecCandidate, RecClient,
} from '../services/api';
import { SkeletonBox, TableSkeleton } from '../components/Skeleton';
import { LoadFailed } from '../components/LoadState'; // [LOAD-STATES 2026-09-29]
import GlassSelect from '../components/GlassSelect';
import { useRealtime } from '../hooks/useRealtime';
import { usePresence } from '../hooks/usePresence';
import { easing, fluid } from '../styles/motion'; // [FLUID-3 2026-10-12] fluid
import { fluidDialog, fluidScrim, fluidCascade, useRowsLand } from '../components/Fluid'; // [FLUID-3 2026-10-12] [FLUID-6 2026-10-12] useRowsLand

/**
 * [PM-RECS 2026-08-25] PM Recommendations — which current PM should take each recurring new sale.
 *
 * The pilot view. For one sale month (July 2026 by default) every recurring new sale is listed
 * with the PM it was actually given to and the PMs this history would rank — and the user
 * compares the two. Nothing here changes an assignment.
 *
 * [SIMPLIFIED 2026-08-25, user's design] The ranked list is a DROPDOWN (best first, each name
 * with its campaign count in the sale's inventory month), and the reasoning lives behind an ⓘ
 * at the end of the row: a centred popup that explains the pick in plain English. Choosing a
 * different name in the dropdown makes the ⓘ explain THAT PM instead — a "what if" — and saves
 * nothing. The earlier inline candidates table was replaced: it answered "how did the model get
 * there", which is the builder's question, not the reader's. See [POPUP v2] below for the five
 * boxes the popup settled on.
 *
 * The PM league, rep-baseline and hand-off tables were all removed at the user's request ("too
 * much for this tab"); the server still computes them because the ranking's lift control depends
 * on them. All arithmetic lives in server/src/services/pmRecommendations.ts; this file only
 * renders it.
 */

/* ── formatting ───────────────────────────────────────────────────────────────── */

const fmtMoney = (n: number | null | undefined) => (n == null ? '—' : `$${Math.round(n).toLocaleString('en-US')}`);
const fmtPct = (r: number | null | undefined) => (r == null ? '—' : `${Math.round(r * 100)}%`);
const MONTHS_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const fmtMonth = (ym: string | null | undefined) => {
  if (!ym) return '—';
  const [y, m] = ym.split('-');
  return `${MONTHS_SHORT[Number(m) - 1] || m} ${y}`;
};
const fmtMonthShort = (ym: string) => MONTHS_SHORT[Number(ym.split('-')[1]) - 1] || ym;
/** YYYY-MM plus n months — the sale month's inventory month is the one after it. */
const ymAdd = (ym: string, n: number) => {
  const [y, m] = ym.split('-').map(Number);
  const d = new Date(Date.UTC(y, m - 1 + n, 1));
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`;
};
const plural = (n: number, one: string, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;
/** "3 points better than …" / "12 points worse than …" / "exactly what …". */

const GREEN = '#1a7f64';
/* [GREEN-ON-DARK 2026-08-27, user] "the green colour ... is too dark and not visible properly,
   just like we have the colour in the i button, make the same colour in the pm recommendation tab".
   GREEN (#1a7f64) is a LIGHT-THEME ink — on the dark panel it sits at roughly 2:1 contrast, under
   the 4.5:1 that small text needs. The popup headings were fixed first; these helpers apply the
   SAME mint everywhere else so the tab reads consistently, rather than repeating the ternary at
   each site and letting them drift apart again. Light theme is unchanged. */
/* [APP-WIDE 2026-08-27] The same values now live in theme.ts as `greenInk` / `greenTint` and
   every tab reads them; these helpers are kept so the call sites below stay as they are, but
   they are aliases of the token, not a second definition that could drift from it. */
const greenInk = (p: any) => p.theme.greenInk as string;
/** Matching tint for a green pill's background — the ink alone is not enough on a dark surface. */
const greenTint = (p: any) => p.theme.greenTint as string;
const AMBER = '#c98a1b';
const RED = '#c0392b';
/* [2026-08-25] "Expected" reads as a warning in amber; the user wants it blue — a status, not a worry. */
const BLUE = '#3b82f6';

type Tone = 'good' | 'warn' | 'bad' | 'muted' | 'info';
const statusTone = (s: string | null | undefined): Tone => {
  const n = String(s ?? '').toLowerCase().replace(/\s+/g, '');
  if (!n) return 'muted';
  if (n === 'cancelled' || n === 'bad') return 'bad';
  if (n === 'retained' || n === 'good' || n.startsWith('converted')) return 'good';
  if (n === 'expected') return 'info';
  return 'warn';
};

/* ── page frame (same as Process Analysis) ────────────────────────────────────── */

const Container = styled.div`
  background: ${(p) => p.theme.surface};
  /* [2026-08-25] This page ENDS in text (the coverage line). The dock covers the bottom ~82px and
     the Go-to-Top pill sits above it to ~130px, so 60px left the last line under the pill; 140px
     keeps it readable. Mobile stays small: the pill is hidden there and the dock sits lower. */
  padding: 14px 20px 140px;
  transition: background 320ms cubic-bezier(0.4, 0, 0.2, 1);
  /* [PHONE-LAYOUT 2026-10-24, user] Phones: 96 px at the end (was 8), so the last note ("Each row is scored on its
     own …") clears the floating dock — it ended under it. */
  @media (max-width: 768px) { padding: 12px 12px 96px; }
`;

const PageHead = styled.div`
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
  padding-bottom: 14px;
  border-bottom: 1px solid ${(p) => p.theme.line};
  margin-bottom: 20px;

  /* [PHONE-LAYOUT 2026-10-24, user] Phones: the page head centred like every other tab — the title and its line,
     then the month box and the History pill (HeadRight) centred under them. */
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: center;
    text-align: center;
    gap: 12px;
  }
`;

const PageTitle = styled.h1`
  font-size: 20px;
  font-weight: 800;
  letter-spacing: -0.3px;
  color: ${(p) => p.theme.text};
  margin: 0 0 4px;
`;

const PageSub = styled.p`
  font-size: 12.5px;
  color: ${(p) => p.theme.muted};
  margin: 0;
  max-width: 780px;
  line-height: 1.5;
`;

const HeadRight = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;

  @media (max-width: 768px) {
    justify-content: center; /* [PHONE-LAYOUT 2026-10-24] centred under the title */
  }
`;

const WindowPill = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 7px 13px;
  border-radius: 999px;
  border: 1px solid ${(p) => p.theme.line};
  background: ${(p) => p.theme.pill};
  font-size: 12px;
  font-weight: 700;
  color: ${(p) => p.theme.text};
  white-space: nowrap;
  /* [LIQUID-GLASS 2026-11-02, user] */
  background-color: ${lgControlBg};
  background-image: ${lgCardSheen};
  border-color: ${(p) => (p.theme.colorScheme === 'dark' ? 'transparent' : 'rgba(22,25,23,0.12)')};
  box-shadow: inset 0 1px 0 ${(p) => (p.theme.colorScheme === 'dark' ? 'rgba(255,255,255,0.12)' : '#ffffff')}, 0 1px 2px rgba(0,0,0,0.08);
`;

const Section = styled.section`
  margin-bottom: 26px;
`;

const Eyebrow = styled.div`
  font-size: 10.5px;
  font-weight: 800;
  letter-spacing: 0.9px;
  text-transform: uppercase;
  color: ${greenInk};
  margin-bottom: 3px;
  /* [PHONE-LAYOUT 2026-10-22] */
  @media (max-width: 768px) { font-size: 11px; }
`;

const SectionTitle = styled.h2`
  font-size: 15.5px;
  font-weight: 750;
  color: ${(p) => p.theme.text};
  margin: 0 0 3px;
`;

const SectionSub = styled.p`
  font-size: 12px;
  color: ${(p) => p.theme.muted};
  margin: 0 0 12px;
  /* [2026-08-25] No max-width: the user wants this line on ONE row on a desktop screen. */
  line-height: 1.5;
`;

const Card = styled.div`
  border: 1px solid ${(p) => p.theme.line};
  border-radius: 14px;
  background: ${(p) => p.theme.surface};
  padding: 14px 16px 16px;
  min-width: 0;
`;

const HowTo = styled(Card)`
  margin-bottom: 18px;
  font-size: 12px;
  line-height: 1.55;
  color: ${(p) => p.theme.text};

  ul { margin: 6px 0 0; padding-left: 18px; }
  li { margin: 2px 0; }
  b { font-weight: 750; }
  /* [LIQUID-GLASS 2026-11-02, user] */
  ${lgTile}
`;

const Scroll = styled.div`
  overflow-x: auto;
  max-width: 100%;
`;

const TablePanel = styled.div`
  border-radius: 10px;
  overflow: hidden;
  background: ${(p) => (p.theme.colorScheme === 'dark' ? 'rgba(255,255,255,0.02)' : 'rgba(255,255,255,0.62)')};
  border: 1px solid ${(p) => (p.theme.colorScheme === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)')};
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 12px;
`;

const Th = styled.th<{ $right?: boolean }>`
  text-align: ${(p) => (p.$right ? 'right' : 'left')};
  font-size: 10px;
  font-weight: 750;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  color: ${(p) => p.theme.muted};
  padding: 10px 10px 8px;
  border-bottom: 1px solid ${(p) => p.theme.line};
  white-space: nowrap;
  /* [PHONE-LAYOUT 2026-10-22] Phones: 11 px headers (10 px is below the phone minimum). */
  @media (max-width: 768px) { font-size: 11px; }
`;

const Td = styled.td<{ $right?: boolean; $strong?: boolean; $muted?: boolean }>`
  text-align: ${(p) => (p.$right ? 'right' : 'left')};
  padding: 7px 10px;
  color: ${(p) => (p.$muted ? p.theme.muted : p.theme.text)};
  font-weight: ${(p) => (p.$strong ? 700 : 500)};
  border-bottom: 1px solid ${(p) => p.theme.line};
  white-space: nowrap;
  vertical-align: middle;
`;

const Pill = styled.span<{ $tone: Tone }>`
  display: inline-block;
  padding: 2px 8px;
  border-radius: 999px;
  font-size: 10.5px;
  font-weight: 750;
  white-space: nowrap;
  color: ${(p) => (p.$tone === 'good' ? greenInk(p) : p.$tone === 'warn' ? AMBER : p.$tone === 'bad' ? RED : p.$tone === 'info' ? BLUE : p.theme.muted)};
  background: ${(p) => (p.$tone === 'good'
    ? greenTint(p)
    : p.$tone === 'warn' ? 'rgba(201,138,27,0.14)'
      : p.$tone === 'bad' ? 'rgba(192,57,43,0.12)'
        : p.$tone === 'info' ? 'rgba(59,130,246,0.14)' : 'rgba(127,127,127,0.12)')};
  /* [PHONE-LAYOUT 2026-10-22] */
  @media (max-width: 768px) { font-size: 11px; }
`;

const Small = styled.span`
  font-size: 11px;
  color: ${(p) => p.theme.muted};
`;

const Empty = styled.div`
  padding: 26px 8px;
  text-align: center;
  font-size: 12px;
  color: ${(p) => p.theme.muted};
`;

const ErrorBox = styled.div`
  padding: 40px 20px;
  text-align: center;
  color: ${(p) => p.theme.danger};
  font-size: 13px;
  font-weight: 600;
`;

const Foot = styled.p`
  font-size: 11px;
  color: ${(p) => p.theme.muted};
  margin: 10px 0 0;
  line-height: 1.5;
`;

/* The dropdown needs room for a long name plus its campaign count. */
const PickWrap = styled.div`
  display: inline-block;
  min-width: 300px;
  button { width: 300px; }

  /* [PHONE-LAYOUT 2026-10-24, user] Phones: the box widens to its whole label ("1. Anvesh . · 29 campaigns · $14,421
     (Oct)" was cut to "… (Oc…" at 300 px); the table already swipes sideways, so the extra width costs nothing. */
  @media (max-width: 768px) {
    button { width: auto; min-width: 300px; }
  }
`;

const InfoBtn = styled.button.attrs<LgPressProps>(LG_PRESS)`
  appearance: none;
  border: 1px solid ${(p) => p.theme.line};
  background: ${(p) => p.theme.pill};
  color: ${greenInk};
  width: 28px;
  height: 28px;
  border-radius: 999px;
  display: inline-grid;
  place-items: center;
  font-size: 13px;
  cursor: pointer;
  transition: transform 120ms ${easing.standard}, box-shadow 120ms ${easing.standard};
  &:hover { transform: translateY(-1px); box-shadow: 0 6px 14px ${(p) => (p.theme.colorScheme === 'dark' ? 'rgba(14,126,78,0.22)' : 'rgba(26,127,100,0.22)')}; }
  &:active { transform: translateY(0); }
  /* [LIQUID-GLASS 2026-11-02, user] */
  position: relative;
  isolation: isolate;
  overflow: hidden;
  ${lgPress}
  background-color: ${lgControlBg};
  background-image: ${lgCardSheen};
`;

/* ── the reasoning popup ──────────────────────────────────────────────────────── */

const Overlay = styled.div<{ $open: boolean }>`
  position: fixed;
  inset: 0;
  z-index: 1100;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  background: ${(p) => (p.theme.colorScheme === 'dark' ? 'rgba(0, 0, 0, 0.35)' : 'rgba(20, 30, 25, 0.18)')};
  backdrop-filter: blur(14px) saturate(140%);
  -webkit-backdrop-filter: blur(14px) saturate(140%);
  /* [FLUID-3 2026-10-12] The shared shade recipe (components/Fluid.tsx): fades in 0.22 s, out 0.18 s, no clicks
     while closing. Was a 180 ms opacity transition. */
  ${(p) => fluidScrim(p.$open)}
`;

const Modal = styled.div<{ $open: boolean }>`
  position: relative;
  background: ${(p) => (p.theme.colorScheme === 'dark'
    ? 'linear-gradient(180deg, rgba(86, 86, 86, 0.42) 0%, rgba(54, 54, 54, 0.55) 100%)'
    : 'linear-gradient(180deg, rgba(255, 255, 255, 0.55) 0%, rgba(230, 233, 231, 0.68) 100%)')};
  backdrop-filter: blur(48px) saturate(180%);
  -webkit-backdrop-filter: blur(48px) saturate(180%);
  border: 1px solid ${(p) => (p.theme.colorScheme === 'dark' ? 'rgba(255,255,255,0.18)' : 'rgba(255,255,255,0.65)')};
  color: ${(p) => p.theme.text};
  border-radius: 20px;
  isolation: isolate;
  box-shadow:
    0 30px 80px ${(p) => (p.theme.colorScheme === 'dark' ? 'rgba(0,0,0,0.55)' : 'rgba(0, 0, 0, 0.22)')},
    0 8px 24px ${(p) => (p.theme.colorScheme === 'dark' ? 'rgba(0,0,0,0.35)' : 'rgba(0, 0, 0, 0.12)')},
    inset 0 1px 0 ${(p) => (p.theme.colorScheme === 'dark' ? 'rgba(255,255,255,0.10)' : 'rgba(255,255,255,0.55)')};
  width: 760px;
  max-width: calc(94vw / var(--app-zoom, 1)); /* [APP-FIT 2026-10-26] the window in page px */
  max-height: calc(88vh / var(--app-zoom, 1)); /* [APP-FIT 2026-10-26] the window in page px */
  overflow-y: auto;
  padding: 22px 26px 22px;
  text-align: left;
  /* [FLUID-3 2026-10-12, user] Rises 12px and grows from 0.95 on the spring (readable after ~0.1 s, open by
     0.36 s), closes in 0.18 s; the heading and the reason cards rise in one after another (20 ms apart, so the
     last of up to seven has landed by ~0.44 s). Was a 0.97 scale settle on transition.modal. */
  ${(p) => fluidDialog({ open: p.$open })}
  ${(p) => (p.$open ? fluidCascade('> *', 0, 20, 6) /* [FLUID-8 2026-10-12] soft: readable at once */ : '')}
  /* [LIQUID-GLASS 2026-11-02, user] */
  ${lgSurface(36)}
  border-color: transparent;
`;

const ModalHead = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 14px;
  margin-bottom: 14px;
`;

const ModalTitle = styled.h3`
  font-size: 17px;
  font-weight: 800;
  letter-spacing: -0.2px;
  margin: 0 0 3px;
  color: ${(p) => p.theme.text};
`;

const ModalSub = styled.div`
  font-size: 12px;
  color: ${(p) => p.theme.muted};
`;

const CloseBtn = styled.button.attrs<LgPressProps>(LG_PRESS)`
  appearance: none;
  border: 1px solid ${(p) => p.theme.line};
  background: ${(p) => p.theme.pill};
  color: ${(p) => p.theme.text};
  width: 30px;
  height: 30px;
  border-radius: 999px;
  display: inline-grid;
  place-items: center;
  cursor: pointer;
  flex: none;
  /* [LIQUID-GLASS 2026-11-02, user] */
  position: relative;
  isolation: isolate;
  overflow: hidden;
  ${lgPress}
  background-color: ${lgControlBg};
  background-image: ${lgCardSheen};
`;

const Block = styled.div`
  padding: 12px 14px;
  border-radius: 12px;
  border: 1px solid ${(p) => p.theme.line};
  background: ${(p) => (p.theme.colorScheme === 'dark' ? 'rgba(255,255,255,0.03)' : 'rgba(255,255,255,0.55)')};
  margin-bottom: 10px;
`;

/* Popup headings. `greenInk` (see the constant, top of file) is the one place the dark-theme mint
   is decided — these headings were where the contrast problem was first reported, and the same
   helper now drives the eyebrow, the good pill and the ⓘ button so they cannot drift apart. */
const BlockTitle = styled.div`
  font-size: 10.5px;
  font-weight: 800;
  letter-spacing: 0.7px;
  text-transform: uppercase;
  color: ${greenInk};
  margin-bottom: 5px;
  /* [PHONE-LAYOUT 2026-10-22] */
  @media (max-width: 768px) { font-size: 11px; }
`;

const Prose = styled.p`
  font-size: 13px;
  line-height: 1.6;
  margin: 0;
  color: ${(p) => p.theme.text};
  b { font-weight: 750; }
`;

const MiniList = styled.ul`
  margin: 8px 0 0;
  padding-left: 18px;
  font-size: 12px;
  line-height: 1.55;
  color: ${(p) => p.theme.text};
  li { margin: 2px 0; }
`;

/* [POPUP v3 2026-08-26] The reasoning is BULLETS, not paragraphs — the user: "too much data in
   it, hard to read which PM is being recommended and why". One fact per line, its number carried
   with it, so a card is scanned rather than read. A bullet with no data behind it is omitted, not
   padded. Cards, chrome and card order are unchanged. */
const Facts = styled.ul`
  margin: 0;
  padding: 0;
  list-style: none;
  font-size: 12.5px;
  line-height: 1.5;
  color: ${(p) => p.theme.text};

  li {
    position: relative;
    padding-left: 14px;
    margin: 4px 0;
  }
  /* A dash rather than a bullet glyph: at this size a disc reads as noise between the figures. */
  li::before {
    content: '';
    position: absolute;
    left: 0;
    top: 0.62em;
    width: 6px;
    height: 1.5px;
    border-radius: 1px;
    background: ${(p) => p.theme.muted};
  }
  b { font-weight: 750; }
`;

/** The one line at the top of a card that names what the card is about. */
const FactLead = styled.div`
  font-size: 13px;
  font-weight: 750;
  line-height: 1.45;
  margin-bottom: 4px;
  color: ${(p) => p.theme.text};
`;

/** A fact list that drops empty entries, so no bullet is ever padded to fill a card. */
const FactList: React.FC<{ items: React.ReactNode[] }> = ({ items }) => {
  const kept = items.filter(Boolean);
  if (!kept.length) return null;
  return <Facts>{kept.map((n, i) => <li key={i}>{n}</li>)}</Facts>;
};

/* ── plain-English reasoning ──────────────────────────────────────────────────── */

/**
 * [POPUP v2 2026-08-25, user's spec] Plain English, the rank is the only score-like number.
 * [PM-RECS v3 2026-08-26] The popup argues THIS ROW. Every PM is scored per row on exactly
 * three inputs — their own retention, their connection with this row's rep, and their campaigns
 * and $ in the month being assigned into — so the boxes are those three, then the comparison:
 *   Why this PM for this payment — the ONE thing that separated them (server `why`)
 *   Own retention — last six complete months, kept % of placed
 *   With the rep's clients — the actual clients, kept or not, against the rep's norm
 *   Campaigns and $ in <M+1> — the book today, judged before this sale, against the average
 *   Against the assigned PM — assigned, their rank, the same three facts
 *   How sure — how much evidence each input rests on
 * No upsell, no refunds, no disputes anywhere (user, 2026-08-26). Scores, points-of-fit,
 * money-per-dollar, similar-size and overall-record boxes stay removed.
 */

/** Room = 1 − capacity index, capped at +1 and open-ended below, exactly as the server scores it. */
const roomOf = (c: RecCandidate) => Math.min(1, 1 - c.capacity.index);

/** Clients from the pair list that are still on the book as of the last observable month. */
const stillOn = (clients: RecClient[], lastObservable: string) =>
  clients.filter((c) => c.lastMonth === lastObservable && statusTone(c.lastStatus) !== 'bad').length;

/* ── [POPUP v3] compact fact builders — one figure-carrying line each ───────────────────────── */

/** Which of the four inputs separates `hi` (ranked higher) from `lo`, by the server's own weights. */
const largestGap = (hi: RecCandidate, lo: RecCandidate, data: PmRecommendationsData): RecCandidate['why'] => {
  const w = data.params.weights;
  const gaps: [RecCandidate['why'], number][] = [
    ['record', hi.record.shrunk - lo.record.shrunk],
    ['fit', w.connection * (hi.connection.liftPts - lo.connection.liftPts)],
    ['capacity', w.room * (roomOf(hi) - roomOf(lo))],
    ['explore', w.explore * ((hi.explore ? 1 : 0) - (lo.explore ? 1 : 0))],
  ];
  gaps.sort((a, b) => b[1] - a[1]);
  return gaps[0][1] > 0 ? gaps[0][0] : 'record';
};

/* [POPUP v5 2026-08-27, user] PLAIN ENGLISH. "If the person is new and doesn't have the idea
   for this then he will not be able to understand." So: no "retention", "lift", "book", "pool",
   "client-months" or "index" anywhere a reader sees it. Each helper returns a phrase a first-time
   reader can follow, with the figure inside it. The arithmetic behind the words is unchanged. */

/** First name only — the popup names people a lot, and "Anvesh ." reads badly. */
const first = (pm: string) => pm.split(' ')[0].replace(/\.$/, '');

/** "keeps 87% of the money placed with them" / "has no clients of their own yet" */
const keepsPhrase = (c: RecCandidate) =>
  c.record.retPct == null ? 'has no clients of their own yet' : `keeps ${Math.round(c.record.retPct)}% of the money placed with them`;

/** "has looked after 3 of Akash Bisht's clients and kept 2" / "has never looked after one of Akash Bisht's clients" */
const repPhrase = (c: RecCandidate, rep: string, data: PmRecommendationsData) => {
  if (c.pair.clients === 0) return `has never looked after one of ${rep}'s clients`;
  const kept = stillOn(c.pair.clients_, data.window.lastObservableMonth);
  const n = c.pair.clients;
  if (n === 1) return `has looked after 1 of ${rep}'s clients and ${kept === 1 ? 'kept it' : 'lost it'}`;
  return `has looked after ${n} of ${rep}'s clients and kept ${kept === n ? 'all of them' : kept === 0 ? 'none' : `${kept}`}`;
};

/** "already looks after 31 clients, about one and a half times the average" */
const loadPhrase = (c: RecCandidate) => {
  const n = c.capacity.count + c.capacity.projectedCount;
  const x = c.capacity.index;
  const vs = x >= 1.9 ? 'about twice the average' : x >= 1.4 ? 'about one and a half times the average' : x > 1.1 ? 'a little above the average' : x >= 0.9 ? 'around the average' : x >= 0.6 ? 'fewer than most' : 'far fewer than most';
  return `already looks after ${plural(n, 'client')} (${vs})`;
};

/** The one thing that decided it, as a sentence comparing the two people. */
const gapLine = (hi: RecCandidate, lo: RecCandidate, kind: RecCandidate['why'], rep: string, data: PmRecommendationsData): React.ReactNode => {
  const H = first(hi.pm), L = first(lo.pm);
  switch (kind) {
    case 'record':
      return <><b>{H}</b> {keepsPhrase(hi)}, while {L} {lo.record.retPct == null ? 'has no track record yet' : `keeps ${Math.round(lo.record.retPct)}%`}.</>;
    case 'fit':
      return <><b>{H}</b> {repPhrase(hi, rep, data)}; {L} {repPhrase(lo, rep, data)}.</>;
    case 'capacity':
      return <><b>{H}</b> has more room — {loadPhrase(hi)}, while {L} {loadPhrase(lo)}.</>;
    case 'explore':
      return <>The two are level, so we prefer <b>{H}</b>: {rep} has never worked with them, and this is how we learn whether they fit.</>;
    default:
      return <><b>{H}</b> is the only PM available.</>;
  }
};

const ReasonModal: React.FC<{
  row: RecCohortRow | null;
  pm: string | null;
  data: PmRecommendationsData;
  open: boolean;
  onClose: () => void;
}> = ({ row, pm, data, open, onClose }) => {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!row) return null;
  const inventoryMonth = ymAdd(data.month, 1);
  const rep = row.rep ?? 'an unknown rep';
  const c: RecCandidate | undefined = row.candidates.find((x) => x.pm === pm) ?? row.candidates[0];
  const scored = row.candidates.filter((x) => x.rank !== null);
  const top = scored[0];
  const assignedC = row.assignedPm ? row.candidates.find((x) => x.pm === row.assignedPm) : undefined;
  const second = row.candidates.find((x) => x.rank === 2);
  const recWindow = data.params.record;

  // [FLUID-3 2026-10-12] Inert whenever it is not open (its opening frame and its close): nothing in it can
  // be used while it is on its way out.
  return createPortal(
    <Overlay $open={open} data-reload-block={open ? '' : undefined} onClick={(e) => { if (e.target === e.currentTarget) onClose(); }} aria-hidden={!open} inert={!open}>
      <Modal $open={open} role="dialog" aria-modal="true">
        <ModalHead>
          <div>
            <ModalTitle>{c ? `Why ${c.pm} for ${row.site}` : row.site}</ModalTitle>
            <ModalSub>
              {fmtMoney(row.initialUsd)} recurring client from <b>{rep}</b> · sold {fmtMonth(data.month)} · starts {fmtMonth(inventoryMonth)}
              {row.assignedPm && <> · assigned to <b>{row.assignedPm}</b></>}
            </ModalSub>
          </div>
          <CloseBtn onClick={onClose} aria-label="Close"><i className="fas fa-times" /></CloseBtn>
        </ModalHead>

        {!c ? (
          <Block><Prose>No PM could be ranked for this sale.</Prose></Block>
        ) : (
          <>
            {/* 1. why this PM for this payment — the decision, as facts */}
            <Block>
              <BlockTitle>Why {c.pm} for this payment</BlockTitle>
              {row.rule === 'ROW' ? (
                <Prose>Every ROW sale goes to <b>{c.pm}</b> — the ROW rule, no ranking.{row.assignedPm && row.assignedPm !== c.pm && <> <b>{row.assignedPm}</b> was assigned instead.</>}</Prose>
              ) : (
                <>
                  {/* [POPUP v4 2026-08-27, user] ONE or TWO lines per card, never more: the
                      deciding fact, and — only where the data says something worth acting on —
                      the rep warning. Rank, size, rep and the confidence level all live in the
                      lead line rather than as bullets of their own. */}
                  <FactLead>
                    {c.rank === 1 ? 'Our first choice' : `Our #${c.rank} choice of ${scored.length}`} for this {fmtMoney(row.initialUsd)} client from {rep}, starting in {fmtMonth(inventoryMonth)}.
                  </FactLead>
                  <FactList items={[
                    c.rank === 1
                      ? (second ? <>Why: {gapLine(c, second, c.why, rep, data)}</> : <><b>{first(c.pm)}</b> is the only PM available.</>)
                      : (top ? <>Why not higher: {gapLine(top, c, largestGap(top, c, data), rep, data)}</> : null),
                    /* [REP-RISK 2026-08-27] The rep is the biggest single driver of whether a new
                       client survives (40% down to 10% across reps), so a client from a weak rep
                       is likely to churn whoever manages it — said here so a poor result later is
                       not read as the PM's failure. Only shown when it is 8+ points off the pool. */
                    row.repRisk && row.repRisk.survivalPct <= row.repRisk.poolPct - 8
                      ? <>Heads-up: only <b>{row.repRisk.survivalPct}%</b> of {rep}'s past clients stayed three months (most reps: {row.repRisk.poolPct}%), so this one may leave whoever looks after it.</>
                      : row.repRisk && row.repRisk.survivalPct >= row.repRisk.poolPct + 8
                        ? <>Good news: <b>{row.repRisk.survivalPct}%</b> of {rep}'s past clients stayed three months (most reps: {row.repRisk.poolPct}%).</>
                        : null,
                  ]} />
                </>
              )}
            </Block>

            {/* 2. input 1 — the PM's own retention */}
            <Block>
              <BlockTitle>How {first(c.pm)} does with their own clients · {fmtMonthShort(recWindow.from)} – {fmtMonthShort(recWindow.to)}</BlockTitle>
              <FactList items={[
                c.record.retPct != null
                  ? <>Keeps <b>{Math.round(c.record.retPct)}%</b> of the money placed with them, across {plural(c.record.clients, 'client')} — {Math.abs(Math.round(c.record.retPct - recWindow.poolScore))} points {c.record.retPct >= recWindow.poolScore ? 'better' : 'worse'} than the team average of {Math.round(recWindow.poolScore)}%.</>
                  : <>New — no clients of their own yet, so we assume a little below the team average of {Math.round(recWindow.poolScore)}% until there is history.</>,
                c.record.retPct != null && c.record.months < recWindow.k && <>That is based on a small number of clients, so we treat it with some caution.</>,
              ]} />
            </Block>

            {/* 3. input 2 — the connection with this row's rep */}
            {row.rule !== 'ROW' && (
              <Block>
                <BlockTitle>{first(c.pm)} and {rep}'s clients</BlockTitle>
                {c.pair.clients > 0 ? (
                  <>
                    <FactList items={[
                      <><b>{first(c.pm)}</b> {repPhrase(c, rep, data)}{stillOn(c.pair.clients_, data.window.lastObservableMonth) === c.pair.clients ? ' — still with us today' : ''}.</>,
                      c.pair.retentionRate != null && c.pair.expectedRetentionRate != null && <>Month to month those clients stayed <b>{fmtPct(c.pair.retentionRate)}</b> of the time; {rep}'s clients usually stay {fmtPct(c.pair.expectedRetentionRate)} — {c.pair.liftPts == null || c.pair.liftPts === 0 ? 'about what you would expect' : c.pair.liftPts > 0 ? `better than expected` : `worse than expected`}.</>,
                    ]} />
                    <MiniList>
                      {c.pair.clients_.map((cl) => (
                        <li key={`${cl.site}|${cl.saleMonth}`}>
                          <b>{cl.site}</b> · sold {fmtMonth(cl.saleMonth)} · {fmtMoney(cl.initialUsd)} · {plural(cl.monthsOnBook, 'mo', 'mo')}{cl.monthsHeld !== cl.monthsOnBook && <> ({cl.monthsHeld} held)</>} · <Pill $tone={statusTone(cl.lastStatus)}>{cl.lastStatus ?? 'unknown'}</Pill>
                        </li>
                      ))}
                    </MiniList>
                  </>
                ) : (
                  <FactList items={[
                    <><b>{first(c.pm)}</b> has never looked after one of {rep}'s clients{c.explore ? ' — giving them this one would show us whether the two work well together' : ''}.</>,
                    c.connection.basis === 'overall' && c.overall.clients > 0
                      ? <>So we go by {first(c.pm)}'s record with everyone's clients: <b>{plural(c.overall.clients, 'client')}</b>{c.overall.retentionRate != null && <>, staying {fmtPct(c.overall.retentionRate)} of the time month to month</>}.</>
                      : c.connection.basis === 'none' ? <>There is no history to go on yet.</> : null,
                  ]} />
                )}
              </Block>
            )}

            {/* 4. input 3 — campaigns and $ in the month being assigned into, judged before this sale */}
            <Block>
              <BlockTitle>{first(c.pm)}'s workload in {fmtMonth(data.capacity.month)}</BlockTitle>
              <FactList items={[
                <><b>{first(c.pm)}</b> {loadPhrase(c)}, worth {fmtMoney(c.capacity.usd + c.capacity.projectedUsd)} a month; a typical PM has {Math.round(c.capacity.avgCount)} worth {fmtMoney(c.capacity.avgUsd)}.{c.capacity.verdict === 'above' ? ' That counts against them a little, but does not rule them out.' : c.capacity.verdict === 'room' ? ' So there is room.' : ''}</>,
                <>Taking this client would make it {c.capacity.withCount}{c.capacity.projectedCount > 0 ? ` (that includes ${plural(c.capacity.projectedCount, 'sale')} higher up this list we have already pointed at them)` : ''}.</>,
              ]} />
            </Block>

            {/* 5. against the assigned PM */}
            {row.assignedPm && (
              <Block>
                <BlockTitle>Compared with {row.assignedPm}, who was given this client</BlockTitle>
                {row.assignedPm === c.pm ? (
                  <Prose><b>{first(c.pm)}</b> is also the PM this client was actually given to.</Prose>
                ) : !assignedC ? (
                  <Prose><b>{row.assignedPm}</b> was given this client{row.assignedIsCurrent ? '' : ' but is no longer on the team'}{row.rule === 'ROW' ? '' : ', and is not among the PMs listed for it'}.</Prose>
                ) : (
                  <FactList items={[
                    <><b>{first(assignedC.pm)}</b> comes <b>#{assignedC.rank} of {scored.length}</b> on our list for this client{row.originalPm && row.originalPm !== row.assignedPm ? ` (the payment was first mapped to ${row.originalPm})` : ''}.</>,
                    <>{first(assignedC.pm)} {keepsPhrase(assignedC)} and {repPhrase(assignedC, rep, data)}; {first(c.pm)} {keepsPhrase(c)} and {repPhrase(c, rep, data)}.</>,
                  ]} />
                )}
              </Block>
            )}

            {/* 6. how sure — what each input rests on */}
            <Block>
              <BlockTitle>How sure are we? {row.rule === 'ROW' ? 'It is a rule' : c.confidence}</BlockTitle>
              {row.rule === 'ROW' ? (
                <Prose>ROW sales always go to the ROW PM, so there is nothing to weigh up.</Prose>
              ) : (
                <FactList items={[
                  <>{first(c.pm)} has {c.record.months >= 3 * recWindow.k ? 'a long track record' : c.record.months >= recWindow.k ? 'a fair track record' : c.record.months > 0 ? 'only a short track record' : 'no track record yet'}{c.pair.clients >= 4 ? `, and has handled several of ${rep}'s clients — so this is well grounded` : c.pair.clients >= 2 ? `, and has handled a couple of ${rep}'s clients — real evidence, but not much of it` : c.pair.clients === 1 ? `, but has handled only one of ${rep}'s clients — one client is a hint, not a pattern` : `, but has never handled one of ${rep}'s clients — so the fit with this rep is a guess`}.</>,
                  c.confidence !== 'High' && <>{c.pair.clients < 2 ? `Two more of ${rep}'s clients through ${first(c.pm)}'s hands would make this firm.` : `A longer record of ${first(c.pm)}'s own would make this firm.`}</>,
                ]} />
              )}
            </Block>
          </>
        )}
      </Modal>
    </Overlay>,
    document.body,
  );
};

/* ── the page ─────────────────────────────────────────────────────────────────── */

const PmRecommendationsPage: React.FC = () => {
  const [month, setMonth] = useState('');           // '' = the server's default (last complete month)
  const params = useMemo(() => (month ? { month } : undefined), [month]);
  const [data, setData] = useState<PmRecommendationsData | null>(
    () => peekCache<PmRecommendationsData>('/pm-recommendations') ?? null,
  );
  const [loading, setLoading] = useState(() => !peekCache<PmRecommendationsData>('/pm-recommendations'));
  const [error, setError] = useState<string | null>(null);
  /** Per sale, the PM chosen in the dropdown (default = #1). Never saved anywhere. */
  const [picks, setPicks] = useState<Record<string, string>>({});
  /** The sale whose ⓘ is open. */
  const [explainSite, setExplainSite] = useState<string | null>(null);
  const presence = usePresence(explainSite, fluid.dialogOutMs); // [FLUID-3 2026-10-12] held for the 0.18 s close (was 200)

  const load = useCallback(async (silent = false) => {
    const seed = silent ? undefined : peekCache<PmRecommendationsData>('/pm-recommendations', params);
    if (!silent) {
      if (seed) { setData(seed); setLoading(false); } else setLoading(true);
      setError(null);
    }
    try {
      setData(await fetchPmRecommendations(month || undefined));
    } catch (e: any) {
      if (silent) return;
      setError(e?.response?.data?.error === 'section_forbidden'
        ? 'You do not have access to PM Recommendations.'
        : 'Failed to load PM Recommendations.');
    } finally {
      if (!silent) setLoading(false);
    }
  }, [month, params]);

  useEffect(() => { load(); }, [load]);
  useRealtime(['inventory', 'payments'], () => load(true));
  // a new month = new sales = new default picks
  useEffect(() => { setPicks({}); }, [data?.month]);

  const monthOptions = useMemo(() => (data ? data.availableMonths : []), [data]);
  const selectedMonth = data?.month ?? month;
  const inventoryMonth = data ? ymAdd(data.month, 1) : '';
  const pickFor = (r: RecCohortRow): string => picks[r.site] ?? r.candidates[0]?.pm ?? '';
  const explainRow = data && presence.value ? data.cohort.find((r) => r.site === presence.value) ?? null : null;

  // [FLUID-6 2026-10-12, user] "pm recommendation also not having animations": when a month's sales land — a tab visit
  // (after the skeleton), or another month picked, from the cache or after its skeleton — every row on screen rises in,
  // top to bottom, as one wave (components/Fluid.tsx). Keyed on the month shown, so the live (silent) refresh, a PM
  // picked in a row's dropdown and the ⓘ popup never replay it.
  const landRef = useRef<HTMLDivElement>(null);
  useRowsLand(landRef, !loading && !error && data && data.cohort.length > 0 ? data.month : null);

  // [LOAD-STATES 2026-09-29, user] A plain line with no retry → the countdown card. "No access" stays
  // a plain message (a final answer, not a failed load).
  if (error) {
    return (
      <Container>
        {error.startsWith('You do not have access')
          ? <ErrorBox>{error}</ErrorBox>
          : <LoadFailed what="PM Recommendations" onRetry={() => load()} />}
      </Container>
    );
  }

  const w = data?.window;

  return (
    <Container>
      <PageHead>
        <div>
          <PageTitle>PM Recommendations</PageTitle>
          <PageSub>
            For every recurring new sale, the PMs this history would rank — best first — next to the PM it
            was actually given to. Built from what each current PM did with the same reps' clients before.
            Nothing here changes an assignment.
          </PageSub>
        </div>
        <HeadRight>
          {loading || !data ? (
            <SkeletonBox $w="150px" $h="30px" $radius="999px" />
          ) : (
            <>
              <GlassSelect liquid deferChange /* [FLUID-8 2026-10-12] instant pick */ value={selectedMonth} onChange={(e: any) => setMonth(e.target.value)}>
                {monthOptions.map((m) => <option key={m} value={m}>{fmtMonth(m)} sales</option>)}
              </GlassSelect>
              <WindowPill>📚 History {fmtMonth(w!.firstInventoryMonth)} – {fmtMonth(w!.lastObservableMonth)} · {w!.handovers.toLocaleString('en-US')} handovers</WindowPill>
            </>
          )}
        </HeadRight>
      </PageHead>

      <HowTo>
        <b>How to read this.</b>
        <ul>
          <li><b>Recommended</b> lists the current PMs best → worst for that client, each with the campaigns they hold today ({data ? fmtMonth(data.window.lastObservableMonth) : 'the current month'}). The first name is the pick; open the list to see who is next.</li>
          <li><b>ROW sales</b> always go to the ROW PM{data?.rowPm ? ` (${data.rowPm})` : ''} — shown, not ranked.</li>
          <li>Press <b>ⓘ</b> at the end of a row for the reasoning in plain English. Choosing another name in the dropdown makes ⓘ explain that PM instead — nothing is saved.</li>
          <li>Outcomes are credited month by month to whoever held the client, and every PM is measured against what their reps' clients usually do.</li>
        </ul>
      </HowTo>

      {/* [2026-08-26] The KPI strip that sat here (sales count · assigned = #1 · top 3 · ROW rule)
          was REMOVED at the user's request — the tab goes straight from "how to read this" to
          the cohort table. Do not bring it back; the per-row rank column carries the same facts. */}

      {/* ── the cohort ───────────────────────────────────────────────────── */}
      <Section>
        <Eyebrow>The pilot</Eyebrow>
        <SectionTitle>{data ? `${fmtMonth(data.month)} recurring new sales — assigned vs recommended` : 'Recurring new sales — assigned vs recommended'}</SectionTitle>
        <SectionSub>One row per recurring new sale, with or without a PM yet. <b>Assigned</b> is the PM on the {data ? fmtMonth(inventoryMonth) : 'next month'} inventory (the PM of record). <b>Recommended</b> lists every current PM in this row's own order — their retention, their history with this rep, and their campaigns and dollars in {data ? fmtMonth(inventoryMonth) : 'the next month'}; open sales are handed down the table as picked. <b>Assigned rank</b> is where the assigned PM sits in it.</SectionSub>
        <TablePanel ref={landRef /* [FLUID-6 2026-10-12] the rows' wave */}>
          {loading || !data ? (
            <TableSkeleton rows={10} columns={[{ label: 'Website' }, { label: 'Rep' }, { label: 'Sale', align: 'right' }, { label: 'Assigned PM' }, { label: 'Now' }, { label: 'Recommended' }, { label: 'Assigned rank' }, { label: '' }]} />
          ) : data.cohort.length === 0 ? (
            <Empty>No recurring new sales in {fmtMonth(data.month)}.</Empty>
          ) : (
            <Scroll>
              <Table>
                <thead>
                  <tr>
                    <Th>Website</Th>
                    <Th>Rep</Th>
                    <Th $right>Sale</Th>
                    <Th>Rep's clients</Th>
                    <Th>Assigned PM</Th>
                    <Th>Now</Th>
                    <Th>Recommended</Th>
                    <Th>Assigned rank</Th>
                    <Th style={{ width: 36 }} />
                  </tr>
                </thead>
                <tbody>
                  {data.cohort.map((r) => {
                    const scoredCount = r.candidates.filter((c) => c.rank !== null).length;
                    const pick = pickFor(r);
                    return (
                      <tr key={r.site}>
                        <Td $strong>{r.site}{r.isRow && <> <Pill $tone="warn">ROW</Pill></>}</Td>
                        <Td $muted={!r.rep}>{r.rep ?? 'unknown'}{r.rep && !r.repActive && <> <Pill $tone="muted">inactive</Pill></>}</Td>
                        <Td $right>{fmtMoney(r.saleUsd)}</Td>
                        {/* [REP-RISK 2026-08-27] Replaces the Size column, which the backtest
                            showed carries no information (27/23/29% survival by size). This one
                            does: 40% down to 10% by rep, against a 26% pool. */}
                        <Td>
                          {r.repRisk
                            ? <Small title={`${r.rep}: ${r.repRisk.clients} past clients followed 3 months; ${r.repRisk.survivalPct}% were still on the book, against ${r.repRisk.poolPct}% across all reps.`}>
                                <Pill $tone={r.repRisk.survivalPct >= r.repRisk.poolPct + 8 ? 'good' : r.repRisk.survivalPct <= r.repRisk.poolPct - 8 ? 'bad' : 'muted'}>{r.repRisk.survivalPct}%</Pill> stay
                              </Small>
                            : <Small>—</Small>}
                        </Td>
                        <Td>
                          {r.assignedPm ?? <Small>none</Small>}
                          {r.assignedPm && !r.assignedIsCurrent && <> <Pill $tone="bad">left</Pill></>}
                          {r.originalPm && r.assignedPm && r.originalPm !== r.assignedPm && <> <Small>(mapped to {r.originalPm})</Small></>}
                          {r.reassigned && <> <Pill $tone="warn">reassigned</Pill></>}
                        </Td>
                        <Td>
                          {r.onInventory
                            ? <><Pill $tone={statusTone(r.nowStatus)}>{r.nowStatus ?? 'unknown'}</Pill> <Small>{fmtMoney(r.nowAmount)} · {fmtMonth(r.nowMonth)}</Small></>
                            : <><Pill $tone="muted">not on inventory yet</Pill> <Small>no PM entered</Small></>}
                        </Td>
                        <Td>
                          {r.candidates.length === 0 ? (
                            <Small>no PM could be scored</Small>
                          ) : (
                            <PickWrap>
                              <GlassSelect liquidList value={pick} onChange={(e: any) => setPicks((p) => ({ ...p, [r.site]: e.target.value }))}>
                                {r.candidates.map((c) => (
                                  <option key={c.pm} value={c.pm}>
                                    {/* [PM-RECS v2] the next-month book in campaigns AND dollars, the two things capacity is judged on */}
                                    {r.rule === 'ROW'
                                      ? `${c.pm} · ROW rule · ${plural(c.capacity.count, 'campaign')} · ${fmtMoney(c.capacity.usd)} (${fmtMonthShort(data.capacity.month)})`
                                      : `${c.rank}. ${c.pm} · ${plural(c.capacity.count, 'campaign')} · ${fmtMoney(c.capacity.usd)} (${fmtMonthShort(data.capacity.month)})${c.explore ? ' · new pair' : ''}`}
                                  </option>
                                ))}
                              </GlassSelect>
                            </PickWrap>
                          )}
                        </Td>
                        <Td>
                          {r.rule === 'ROW'
                            ? (r.assignedPm === data.rowPm ? <Pill $tone="good">ROW PM ✓</Pill> : <Pill $tone="bad">not the ROW PM</Pill>)
                            : r.assignedRank === null ? <Small>{r.assignedPm ? 'not in the list' : '—'}</Small>
                              : <Pill $tone={r.assignedRank === 1 ? 'good' : r.assignedRank <= 3 ? 'warn' : 'bad'}>#{r.assignedRank} of {scoredCount}</Pill>}
                        </Td>
                        <Td $right>
                          {r.candidates.length > 0 && (
                            <InfoBtn onClick={() => setExplainSite(r.site)} aria-label={`Why ${pick} for ${r.site}`} title="Why this PM?">
                              <i className="fas fa-info" aria-hidden="true" />
                            </InfoBtn>
                          )}
                        </Td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
            </Scroll>
          )}
        </TablePanel>
        {data && data.notOnInventory.length > 0 && (
          <Foot>{data.notOnInventory.length} sale{data.notOnInventory.length === 1 ? ' is' : 's are'} not on {fmtMonth(data.capacity.month)}'s inventory yet (no PM entered) — still listed above and ranked on the sale amount: {data.notOnInventory.map((n) => `${n.site} (${fmtMoney(n.saleUsd)}${n.rep ? `, ${n.rep}` : ''})`).join(', ')}.</Foot>
        )}
      </Section>

      {data && (
        <Foot>
          Each row is scored on its own: the PM's retention over {fmtMonth(data.params.record.from)} – {fmtMonth(data.params.record.to)} (kept % of placed; pool {Math.round(data.params.record.poolScore)}%), their history with that row's rep (deliberately cautious — a pairing counts half only at {data.params.weights.pairK} shared clients; a single swing capped at ±{data.params.weights.liftCap} points), and their campaigns and dollars in {fmtMonth(data.capacity.month)} against the {data.capacity.poolSize}-PM average ({Math.round(data.capacity.avgCount)} / {fmtMoney(data.capacity.avgUsd)}, ROW PM excluded), judged before the sale. No upsell, no refunds, no disputes. Every current PM is listed; open sales are handed down the table as picked.
          {' '}Coverage: {data.window.handovers.toLocaleString('en-US')} handovers since {fmtMonth(data.window.firstInventoryMonth)},
          {' '}{data.window.handoversWithRep.toLocaleString('en-US')} with a rep ({data.window.repFromPayments.toLocaleString('en-US')} from the payment, {data.window.repFromDeals.toLocaleString('en-US')} from the HubSpot deal mirror{data.window.dealsMirrorAvailable ? '' : ' — mirror unavailable on this build'}).
          {' '}Roster: {data.roster.pms.length} current PMs, {data.roster.activeReps.length} active reps.
        </Foot>
      )}

      {data && presence.mounted && (
        <ReasonModal
          row={explainRow}
          pm={explainRow ? pickFor(explainRow) : null}
          data={data}
          open={presence.entered && !presence.closing}
          onClose={() => setExplainSite(null)}
        />
      )}
    </Container>
  );
};

export default PmRecommendationsPage;
